import React, { useEffect, useState } from "react";
import API_BASE from "../config/api";
import SetupGuideModal from "./SetupGuideModal";
import BizHomeTab from "./BizHomeTab";
import BizAdminTab from "./BizAdminTab";
import BizProductTab from "./BizProductTab";

/*
  Dashboard page to manage the customer's Business Website (IndiaMART style).
  CR B1: now mirrors PersonalWebsiteManager's tab layout (Home / Admin / Product)
  instead of one long scrolling form, while keeping a single shared "Save
  Website" action so save_business_site.php doesn't need to change shape.
  props: userData, listinguid
*/
export default function BusinessWebsiteManager({ userData, listinguid, onBack }) {
    const localUser = JSON.parse(localStorage.getItem("user") || "{}");
    const personaluid = userData?.personaluid || localUser?.personaluid;

    const [tab, setTab] = useState("home");

    const [form, setForm] = useState({
        name: "", tagline: "", logo: "", cover: "", phone: "", email: "",
        address: "", website: "", about: "",
        linkfacebook: "", linkinstagram: "", linklinkedin: "",
        nature_of_business: "", year_established: "", gst_no: "", legal_status: "", employees: "", annual_turnover: "",
        youtube_link: "", gallery_video: "", brochure_file: "", brochure_link: "", theme_color: "classic-navy",
        trustseal: false, manual_rating: "", // B11
    });
    const [products, setProducts] = useState([{ category: "", name: "", image: "", price: "", unit: "", description: "" }]);
    const [hours, setHours] = useState([{ day: "", time: "" }]);
    const [gallery, setGallery] = useState([""]);
    const [domain, setDomain] = useState("");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    useEffect(() => {
        if (!personaluid) return;
        fetch(`${API_BASE}/api/get_business_site.php?personaluid=${encodeURIComponent(personaluid)}`)
            .then((r) => r.json())
            .then((res) => {
                if (res.domain) setDomain(res.domain);
                if (res.success && res.data) {
                    const d = res.data;
                    setForm({
                        name: d.name || "", tagline: d.tagline || "", logo: d.logo || "", cover: d.cover || "",
                        phone: d.phone || "", email: d.email || "", address: d.address || "", website: d.website || "",
                        about: d.about || "", linkfacebook: d.linkfacebook || "", linkinstagram: d.linkinstagram || "",
                        linklinkedin: d.linklinkedin || "",
                        nature_of_business: d.nature_of_business || "", year_established: d.year_established || "",
                        gst_no: d.gst_no || "", legal_status: d.legal_status || "", employees: d.employees || "",
                        annual_turnover: d.annual_turnover || "",
                        youtube_link: d.youtube_link || "", gallery_video: d.gallery_video || "", brochure_file: d.brochure_file || "",
                        brochure_link: d.brochure_link || "", theme_color: d.theme_color || "classic-navy",
                        trustseal: Number(d.trustseal) === 1, // B11
                        manual_rating: d.manual_rating ?? "",
                    });
                    setProducts(d.products?.length ? d.products : [{ category: "", name: "", image: "", price: "", unit: "", description: "" }]);
                    setHours(d.hours?.length ? d.hours : [{ day: "", time: "" }]);
                    setGallery(d.gallery?.length ? d.gallery : [""]);
                }
            })
            .catch(() => { });
    }, [personaluid]);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const updList = (list, setList, i, key, val) => { const c = [...list]; c[i] = { ...c[i], [key]: val }; setList(c); };

    const handleSave = async () => {
        if (!form.name.trim()) { setMsg({ type: "warning", text: "Business name is required." }); setTab("home"); return; }

        const yt = (form.youtube_link || "").trim();
        if (yt && !/^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//i.test(yt)) {
            setMsg({ type: "warning", text: "YouTube field must be a valid YouTube link." });
            setTab("home");
            return;
        }

        setSaving(true); setMsg(null);
        try {
            const res = await fetch(`${API_BASE}/api/save_business_site.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    personaluid, listinguid, ...form, youtube_link: yt,
                    products: products.filter((p) => (p.name || "").trim() !== ""),
                    hours: hours.filter((h) => (h.day || "").trim() !== ""),
                    gallery: gallery.filter((g) => (g || "").trim() !== ""),
                }),
            });
            const data = await res.json();
            setSaving(false);
            if (data.success) { setDomain(data.domain); setMsg({ type: "success", text: "Business website saved successfully." }); }
            else setMsg({ type: "danger", text: data.message || "Could not save." });
        } catch { setSaving(false); setMsg({ type: "danger", text: "Network error while saving." }); }
    };

    const tabs = [
        ["home", "Home"],
        ["admin", "Admin"],
        ["product", "Product"],
    ];

    return (
        <div className="container-fluid py-2" style={{ maxWidth: 980 }}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                    onClick={() => onBack && onBack()}
                >
                    <i className="bi bi-arrow-left"></i> Back
                </button>
                <h5 className="fw-bold mb-0">Business Website</h5>
                <div className="d-flex align-items-center gap-2">
                    <SetupGuideModal
                        storageKey={`dn_guide_business_${personaluid || "me"}`}
                        title="Set up your Business Website"
                        intro="Welcome! Fill these sections to launch your business website. It only takes a few minutes."
                        steps={[
                            { title: "Home", desc: "Business name, tagline, contact info, logo, cover image, About and a YouTube video link." },
                            { title: "Admin", desc: "Company factsheet, PAN/VAT number, theme colour, Portfolio/Catalog/Brochure, hours and social links." },
                            { title: "Product", desc: "Add products with photos, pricing, your photo gallery, and an optional business video." },
                        ]}
                    />
                    {domain && (
                        <a className="btn btn-sm btn-outline-success" target="_blank" rel="noreferrer" href={`/${domain}`}>
                            <i className="bi bi-box-arrow-up-right me-1" /> View Live Website
                        </a>
                    )}
                </div>
            </div>

            <ul className="nav nav-tabs mb-3">
                {tabs.map(([k, label]) => (
                    <li className="nav-item" key={k}>
                        <button className={`nav-link ${tab === k ? "active" : ""}`} onClick={() => setTab(k)} type="button">
                            {label}
                        </button>
                    </li>
                ))}
            </ul>

            {msg && <div className={`alert alert-${msg.type} py-2`}>{msg.text}</div>}

            {tab === "home" && <BizHomeTab form={form} set={set} personaluid={personaluid} />}
            {tab === "admin" && (
                <BizAdminTab
                    form={form} set={set}
                    hours={hours} setHours={setHours} updList={updList}
                    personaluid={personaluid}
                />
            )}
            {tab === "product" && (
                <BizProductTab
                    products={products} setProducts={setProducts} updList={updList}
                    gallery={gallery} setGallery={setGallery}
                    form={form} set={set}
                    personaluid={personaluid}
                />
            )}

            <div className="d-flex justify-content-end gap-2 mb-4">
                <button className="btn btn-primary px-4" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Website"}</button>
            </div>
        </div>
    );
}